import React, { useEffect, useId, useState } from 'react';
import { ExternalLink, History } from 'lucide-react';
import { Avatar, Dialog, DialogBody, DialogHeader, EmptyState, Skeleton } from '../../../components/ui';
import { issuesErrorMessage, stanceHistory } from '../issuesApi';
import type { StanceView, SubjectRef } from '../../../types/issues';
import { CONFIDENCE_LABEL, SOURCE_LABEL, formatDay, stanceTime, timeAgo } from '../components/format';
import { PositionPill } from '../components/PositionMark';

/**
 * Every reading ever recorded for one actor on one issue, newest first.
 * Nothing here can be edited: the record is append-only by design.
 */
export const StanceHistoryDialog: React.FC<{
  issueId: string;
  subject: SubjectRef;
  onClose: () => void;
}> = ({ issueId, subject, onClose }) => {
  const titleId = useId();
  const [entries, setEntries] = useState<StanceView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    stanceHistory(issueId, subject.type, subject.id)
      .then((history) => {
        if (cancelled) return;
        setEntries([...(history?.entries || [])].sort((a, b) => stanceTime(b) - stanceTime(a)));
        setError(null);
      })
      .catch((err) => !cancelled && setError(issuesErrorMessage(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [issueId, subject.type, subject.id]);

  return (
    <Dialog open onClose={onClose} labelledBy={titleId} handle className="max-h-[88dvh] sm:max-w-lg">
      <DialogHeader id={titleId} eyebrow="Position history" title={subject.label} onClose={onClose} />
      <DialogBody className="pb-5">
        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        {!loading && error && (
          <p role="alert" className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
            {error}
          </p>
        )}
        {!loading && !error && !entries.length && <EmptyState icon={History} title="No readings recorded" />}
        {!loading && !error && entries.length > 0 && (
          <ol className="space-y-3">
            {entries.map((entry) => (
              <li key={entry.id} className="flex gap-3">
                <Avatar name={subject.label} kind={subject.type} size="xs" className="mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <PositionPill position={entry.position} short />
                    <span className="text-[12px] text-neutral-400">
                      {formatDay(entry.asOf)} · {timeAgo(entry.asOf)}
                    </span>
                  </div>
                  {entry.rationale && <p className="mt-1 text-[12.5px] leading-snug text-neutral-600">{entry.rationale}</p>}
                  <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11.5px] text-neutral-400">
                    <span>{SOURCE_LABEL[entry.source]}</span>
                    <span aria-hidden="true">·</span>
                    <span>{CONFIDENCE_LABEL[entry.confidence]} confidence</span>
                    {entry.recordedBy && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>{entry.recordedBy.name}</span>
                      </>
                    )}
                    {entry.evidenceUrl && (
                      <a
                        href={entry.evidenceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                      >
                        <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                        Evidence
                      </a>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        )}
      </DialogBody>
    </Dialog>
  );
};

export default StanceHistoryDialog;
